import { useTRPC } from "@/trpc/client";
import { useQuery } from "@tanstack/react-query";

/**
 * KPI definitions. Omit `salesTeamId` for the full list (the editor); pass a
 * team id — or `null` for the org-wide set — to get the definitions that
 * actually apply to that scope.
 */
export function useKpiDefinitions(
  businessId: string | undefined,
  salesTeamId?: string | null,
) {
  const trpc = useTRPC();
  return useQuery(
    trpc.intelligence.kpi.listDefinitions.queryOptions(
      {
        businessId: businessId ?? "",
        ...(salesTeamId === undefined ? {} : { salesTeamId }),
      },
      { enabled: Boolean(businessId) },
    ),
  );
}

export function useKpiEntries(
  businessId: string | undefined,
  periodStart: string | undefined,
  periodEnd: string | undefined,
  salesTeamId?: string,
) {
  const trpc = useTRPC();
  return useQuery(
    trpc.intelligence.kpi.listEntriesForPeriod.queryOptions(
      {
        businessId: businessId ?? "",
        periodStart: periodStart ?? "",
        periodEnd: periodEnd ?? "",
        ...(salesTeamId ? { salesTeamId } : {}),
      },
      { enabled: Boolean(businessId && periodStart && periodEnd) },
    ),
  );
}

/**
 * The nightly KPI status snapshot — status/trend per KPI per subject, plus
 * sparkline points. Null until the first nightly run. Recomputed once a
 * night, so a generous staleTime avoids refetch churn between views.
 */
export function useKpiStatus(
  businessId: string | undefined,
  opts: { salesTeamId?: string } = {},
) {
  const trpc = useTRPC();
  return useQuery(
    trpc.intelligence.kpi.status.queryOptions(
      {
        businessId: businessId ?? "",
        ...(opts.salesTeamId ? { salesTeamId: opts.salesTeamId } : {}),
      },
      { enabled: Boolean(businessId), staleTime: 5 * 60 * 1000 },
    ),
  );
}

/** Entries across the last N periods — the data behind the trend charts. */
export function useKpiHistory(
  businessId: string | undefined,
  opts: {
    salesTeamId?: string;
    memberId?: string;
    periodCount?: number;
  } = {},
) {
  const trpc = useTRPC();
  return useQuery(
    trpc.intelligence.kpi.listHistory.queryOptions(
      {
        businessId: businessId ?? "",
        periodCount: opts.periodCount ?? 6,
        ...(opts.salesTeamId ? { salesTeamId: opts.salesTeamId } : {}),
        ...(opts.memberId ? { memberId: opts.memberId } : {}),
      },
      { enabled: Boolean(businessId) },
    ),
  );
}
